// RegisterScreen.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  findNodeHandle,
  Keyboard,
  KeyboardAvoidingView,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  Pressable,
  Modal,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { format } from "date-fns";

import { DivineBackground } from "../../shared/ui/DivineBackground";
import { Header } from "../../shared/ui/Header";
import { Button, Input } from "../../shared/ui/FormElements";
import { ToastHost } from "../../shared/ui/ToastHost";
import { AuthStackParamList } from "../../navigation/navigationTypes";
import { UserRegistrationService } from "../../services/UserRegistrationService";
import { Flash } from "../../shared/utils/Flash";
import { RegistrationOtpModel } from "../../models/RegistrationOtpModel";
import { OtpChannelInfoModel } from "../../models/OtpVerificationModel";
import { useAuth } from "../../../context/AuthContext";
import { UserRegistrationModel } from "../../models/UserRegistrationModel";
import Ionicons from "react-native-vector-icons/Ionicons";
import {
  OTP_MAX_LENGTH,
  OTP_PLACEHOLDER,
  getMaskedOtpLabel,
  hasAnyVerificationChannel,
  hasOtpValue,
  normalizeOtpChannelInfo,
} from "../../shared/utils/verification";

/** ✅ CityId of "Other" (use your real Other cityId if API provides different) */
const OTHER_CITY_ID = "00000000-0000-0000-0000-000000000000";
const EMPTY_OTP_CHANNELS: OtpChannelInfoModel = {
  email: false,
  mobile: false,
  maskedEmail: null,
  maskedMobileNumber: null,
};

export interface SelectOption {
  id: string;
  name: string;
}

type Nav = NativeStackNavigationProp<AuthStackParamList, "Register">;
const RESEND_OTP_SECONDS = 5 * 60;
const DOB_PLACEHOLDER = "DD-MM-YYYY";

const formatDobInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
};

const getDobValidationError = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "Date of Birth is required.";
  }

  if (!/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    return `Enter Date of Birth in ${DOB_PLACEHOLDER} format.`;
  }

  const [dayText, monthText, yearText] = trimmed.split("-");
  const day = Number(dayText);
  const month = Number(monthText);
  const year = Number(yearText);
  const currentYear = new Date().getFullYear();

  if (month < 1 || month > 12) {
    return "Month must be between 01 and 12.";
  }

  if (day < 1 || day > 31) {
    return "Date must be between 01 and 31.";
  }

  if (year >= currentYear) {
    return `Year must be before ${currentYear}.`;
  }

  const candidate = new Date(year, month - 1, day);

  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return "Enter a valid calendar date.";
  }

  return "";
};

const getMobileValidationError = (value: string) => {
  const trimmed = value.replace(/\D/g, "");

  if (!trimmed) {
    return "Mobile Number is required.";
  }

  if (!/^\d{10}$/.test(trimmed)) {
    return "Enter a valid 10-digit mobile number.";
  }

  if (/^[0-5]/.test(trimmed)) {
    return "Mobile number must start with 6, 7, 8, or 9.";
  }

  return "";
};

const getEmailValidationError = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "Email is required.";
  }

  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  if (!emailRegex.test(trimmed)) {
    return "Enter a valid email address.";
  }

  return "";
};

const sortOptionsByName = (options: SelectOption[]) =>
  [...options].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );

const sortCitiesWithOtherLast = (options: SelectOption[]) => {
  const regularOptions = options.filter(
    (item) => String(item.id).toLowerCase() !== OTHER_CITY_ID.toLowerCase()
  );
  const otherOptions = options.filter(
    (item) => String(item.id).toLowerCase() === OTHER_CITY_ID.toLowerCase()
  );

  return [...sortOptionsByName(regularOptions), ...otherOptions];
};

function BlurBackdrop() {
  let BlurViewComp: any = null;

  try {
    BlurViewComp = require("expo-blur").BlurView;
  } catch { }

  if (!BlurViewComp) {
    try {
      BlurViewComp = require("@react-native-community/blur").BlurView;
    } catch { }
  }

  if (BlurViewComp) {
    return (
      <BlurViewComp
        style={StyleSheet.absoluteFillObject}
        blurType="light"
        blurAmount={14}
        reducedTransparencyFallbackColor="rgba(0,0,0,0.35)"
      />
    );
  }
  return <View style={styles.otpBackdropFallback} />;
}

/** ✅ Inline searchable dropdown (floating card) */
function SearchSelect(props: {
  label: string;
  required?: boolean;
  placeholder: string;
  valueLabel: string;
  valueId?: string | null;
  options: SelectOption[];
  alwaysVisibleOptionIds?: string[];
  disabled?: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (opt: SelectOption) => void;
  searchPlaceholder?: string;
  dropdownDirection?: "down" | "up";
}) {
  const {
    label,
    required,
    placeholder,
    valueLabel,
    valueId,
    options,
    alwaysVisibleOptionIds,
    disabled,
    isOpen,
    onToggle,
    onSelect,
    searchPlaceholder,
    dropdownDirection = "down",
  } = props;

  const [q, setQ] = useState("");

  useEffect(() => {
    if (!isOpen) setQ("");
  }, [isOpen]);

  const filtered = useMemo(() => {
    const pinnedIds = new Set(
      (alwaysVisibleOptionIds ?? []).map((id) => String(id).toLowerCase())
    );
    const pinnedOptions = options.filter((x) =>
      pinnedIds.has(String(x.id).toLowerCase())
    );
    const searchableOptions = options.filter(
      (x) => !pinnedIds.has(String(x.id).toLowerCase())
    );
    const s = q.trim().toLowerCase();
    if (!s) return [...pinnedOptions, ...searchableOptions];

    const matchedOptions = searchableOptions.filter((x) =>
      x.name.toLowerCase().includes(s)
    );

    return [...pinnedOptions, ...matchedOptions];
  }, [alwaysVisibleOptionIds, q, options]);

  return (
    <View style={[styles.ddWrap, isOpen ? styles.ddWrapOpen : null]}>
      <Text style={styles.ddLabel}>
        {label} {required ? <Text style={styles.ddReq}>*</Text> : null}
      </Text>

      <Pressable
        onPress={() => !disabled && onToggle()}
        style={[
          styles.ddField,
          disabled ? styles.ddFieldDisabled : null,
          isOpen ? styles.ddFieldOpen : null,
        ]}
      >
        <Text style={[styles.ddValue, !valueLabel ? styles.ddPlaceholder : null]}>
          {valueLabel || placeholder}
        </Text>
        <Text style={styles.ddChevron}>{isOpen ? "˄" : "˅"}</Text>
      </Pressable>

      {isOpen ? (
        <View
          style={[
            dropdownDirection === "up"
              ? styles.ddFloatCardUp
              : styles.ddFloatCardDown,
          ]}
          pointerEvents="box-none"
        >
          <View style={styles.ddListCard}>
            <View style={styles.ddSearchWrap}>
              <Input
                placeholder={searchPlaceholder || "Search..."}
                value={q}
                onChangeText={setQ}
                style={styles.ddSearchInput}
              />
            </View>

            <ScrollView
              style={styles.ddScroll}
              nestedScrollEnabled
              showsVerticalScrollIndicator
              keyboardShouldPersistTaps="handled"
            >
              {filtered.length === 0 ? (
                <Text style={styles.ddEmpty}>No items</Text>
              ) : (
                filtered.map((opt) => {
                  const selected = !!valueId && String(valueId) === String(opt.id);
                  return (
                    <Pressable
                      key={opt.id}
                      style={[styles.ddItem, selected ? styles.ddItemSelected : null]}
                      onPress={() => onSelect(opt)}
                    >
                      <View style={styles.ddRadioRow}>
                        <View style={[styles.ddRadio, selected ? styles.ddRadioOn : null]} />
                        <Text
                          style={[
                            styles.ddItemText,
                            selected ? styles.ddItemTextSelected : null,
                          ]}
                        >
                          {opt.name}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })
              )}
            </ScrollView>
          </View>
        </View>
      ) : null}
    </View>
  );
}

export function RegisterScreen() {
  const navigation = useNavigation<Nav>();
  const { logout } = useAuth();

  const [loading, setLoading] = useState(false);

  // ---- Form fields ----
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    dob: "" as string, // dd-MM-yyyy (display)
    dobDate: null as Date | null, // actual date object
    email: "",
    profession: "",
    addressLine1: "",
    addressLine2: "",
    cityName: "", // for Other
    familyDetail: "",
    password: "", // mpin
    confirmMpin: "",
  });

  // ---- dropdown selections (ids) ----
  const [stateId, setStateId] = useState<string | null>(null);
  const [cityId, setCityId] = useState<string | null>(null);
  const [maritalStatusId, setMaritalStatusId] = useState<string | null>(null);

  // ✅ Gender must be SENT as VALUE (string), so store selected VALUE
  const [genderId, setGenderValue] = useState<string | null>(null);

  // ---- dropdown labels ----
  const [stateName, setStateName] = useState("");
  const [cityLabel, setCityLabel] = useState("");
  const [maritalLabel, setMaritalLabel] = useState("");
  const [genderLabel, setGenderLabel] = useState("");

  // ---- dropdown open flags ----
  const [openGender, setOpenGender] = useState(false);
  const [openMarital, setOpenMarital] = useState(false);
  const [openState, setOpenState] = useState(false);
  const [openCity, setOpenCity] = useState(false);

  // ---- options ----
  const [states, setStates] = useState<SelectOption[]>([]);
  const [cities, setCities] = useState<SelectOption[]>([]);
  const [maritals, setMaritals] = useState<SelectOption[]>([]);
  const [genders, setGenders] = useState<SelectOption[]>([]);

  // ---- loading flags ----
  const [loadingState, setLoadingState] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);
  const [loadingMarital, setLoadingMarital] = useState(false);
  const [loadingGender, setLoadingGender] = useState(false);

  // ---- OTP ----
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [mobileOtp, setMobileOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const [dobError, setDobError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [registrationOtpToken, setRegistrationOtpToken] = useState("");
  const [otpDeliveryInfo, setOtpDeliveryInfo] = useState<OtpChannelInfoModel>(EMPTY_OTP_CHANNELS);
  const [resendTimer, setResendTimer] = useState(0);
  const [registrationPayload, setRegistrationPayload] = useState<UserRegistrationModel | null>(null);

  // ---- Terms ----
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const scrollRef = useRef<ScrollView | null>(null);
  const fieldLayouts = useRef<Record<string, number>>({});
  const inputRefs = useRef<Record<string, TextInput | null>>({});

  const setField = (k: keyof typeof form, v: any) => setForm((p) => ({ ...p, [k]: v }));
  const setInputRef = (field: string) => (ref: TextInput | null) => {
    inputRefs.current[field] = ref;
  };
  const setFieldLayout = (field: string) => (event: any) => {
    fieldLayouts.current[field] = event.nativeEvent.layout.y;
  };
  const scrollToFieldLayout = (field: string) => {
    const y = fieldLayouts.current[field];

    if (typeof y === "number") {
      scrollRef.current?.scrollTo({ y: Math.max(0, y - 96), animated: true });
    }
  };
  const scrollInputIntoView = (field: string) => {
    const input = inputRefs.current[field];
    const scrollResponder = scrollRef.current as any;
    const nodeHandle = input ? findNodeHandle(input) : null;

    if (scrollResponder?.scrollResponderScrollNativeHandleToKeyboard && nodeHandle) {
      requestAnimationFrame(() => {
        scrollResponder.scrollResponderScrollNativeHandleToKeyboard(nodeHandle, 120, true);
      });
    }
  };
  const focusRegisterInput = (field: string) => {
    scrollInputIntoView(field);
    requestAnimationFrame(() => {
      inputRefs.current[field]?.focus();
    });
  };
  const focusFirstInvalidRegisterField = () => {
    if (!form.firstName.trim()) return focusRegisterInput("firstName");
    if (!form.lastName.trim()) return focusRegisterInput("lastName");
    if (!form.dobDate || dobError) {
      return focusRegisterInput("dob");
    }
    if (getMobileValidationError(form.mobileNumber)) {
      return focusRegisterInput("mobileNumber");
    }

    if (getEmailValidationError(form.email)) {
      return focusRegisterInput("email");
    }
    if (!form.profession.trim()) return focusRegisterInput("profession");
    if (!genderId) {
      Keyboard.dismiss();
      return scrollToFieldLayout("gender");
    }
    if (!maritalStatusId) {
      Keyboard.dismiss();
      return scrollToFieldLayout("marital");
    }
    if (!form.addressLine1.trim()) return focusRegisterInput("addressLine1");
    if (!stateId) {
      Keyboard.dismiss();
      return scrollToFieldLayout("state");
    }
    if (!cityId) {
      Keyboard.dismiss();
      return scrollToFieldLayout("city");
    }
    if (isOtherCity && !form.cityName.trim()) return focusRegisterInput("cityName");
    if (form.password.length !== 6) return focusRegisterInput("password");
    if (form.confirmMpin.length !== 6 || form.confirmMpin !== form.password) {
      return focusRegisterInput("confirmMpin");
    }
    if (!termsAccepted) {
      Keyboard.dismiss();
      return scrollToFieldLayout("terms");
    }

    Keyboard.dismiss();
  };
  const syncDobField = (value: string) => {
    const formattedValue = formatDobInput(value);
    const validationError = formattedValue.length === 10 ? getDobValidationError(formattedValue) : "";

    setField("dob", formattedValue);
    setField("dobDate", validationError || formattedValue.length !== 10 ? null : new Date(
      Number(formattedValue.slice(6, 10)),
      Number(formattedValue.slice(3, 5)) - 1,
      Number(formattedValue.slice(0, 2))
    ));

    setDobError(formattedValue.length === 10 ? validationError : "");
  };

  const syncMobileField = (value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 10);
    setField("mobileNumber", cleaned);
    setMobileError(cleaned.length === 10 ? getMobileValidationError(cleaned) : "");
  };

  const validateMobileField = () => {
    const validationError = getMobileValidationError(form.mobileNumber);
    setMobileError(validationError);
    return !validationError;
  };

  const syncEmailField = (value: string) => {
    const cleaned = value.trimStart();
    setField("email", cleaned);
    setEmailError(cleaned.trim() ? getEmailValidationError(cleaned) : "");
  };

  const validateEmailField = () => {
    const validationError = getEmailValidationError(form.email);
    setEmailError(validationError);
    return !validationError;
  };

  const validateDobField = () => {
    const validationError = getDobValidationError(form.dob);
    setDobError(validationError);
    return !validationError;
  };

  const closeAllDropdowns = () => {
    setOpenGender(false);
    setOpenMarital(false);
    setOpenState(false);
    setOpenCity(false);
  };
  const resetRegistrationOtpState = () => {
    setShowOtpModal(false);
    setRegistrationOtpToken("");
    setOtpDeliveryInfo(EMPTY_OTP_CHANNELS);
    setMobileOtp("");
    setEmailOtp("");
    setOtpErrorMessage("");
    setResendTimer(0);
    setRegistrationPayload(null);
  };
  const handleBackToLogin = async () => {
    resetRegistrationOtpState();
    await logout({ deleteDevice: false });
    navigation.navigate("Login");
  };
  const showOtpError = (message: string, description?: string) => {
    setOtpErrorMessage(message);
    Flash.error(message, description);
  };

  const OTHER_CITY_OPTION: SelectOption = useMemo(
    () => ({ id: OTHER_CITY_ID, name: "Other" }),
    []
  );

  // ✅ Load State + Marital + Gender from API
  useEffect(() => {
    (async () => {
      try {
        setLoadingState(true);
        const res = await UserRegistrationService.getStates();
        const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
          id: String(x.id ?? x.stateId ?? x.value ?? ""),
          name: String(x.name ?? x.stateName ?? x.label ?? ""),
        }));
        setStates(sortOptionsByName(list.filter((x) => x.id && x.name)));
      } catch {
        setStates([]);
      } finally {
        setLoadingState(false);
      }
    })();

    (async () => {
      try {
        setLoadingMarital(true);
        const res = await UserRegistrationService.getEnumeration("MaritalStatus");
        const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
          id: String(x.enumerationId ?? x.id ?? ""),
          name: String(x.value ?? x.name ?? ""),
        }));
        setMaritals(list.filter((x) => x.id && x.name));
      } catch {
        setMaritals([]);
      } finally {
        setLoadingMarital(false);
      }
    })();

    // ✅ Gender from API (send VALUE not id)
    (async () => {
      try {
        setLoadingGender(true);
        const res = await UserRegistrationService.getEnumeration("Gender");
        const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
          id: String(x.enumerationId ?? x.id ?? ""), // keep for selection highlight
          name: String(x.value ?? x.name ?? ""), // VALUE to send
        }));
        setGenders(list.filter((x) => x.id && x.name));
      } catch {
        setGenders([]);
      } finally {
        setLoadingGender(false);
      }
    })();
  }, []);

  // ✅ Load cities based on stateId + always append "Other"
  const loadCitiesByState = async (sid: string) => {
    setLoadingCity(true);
    try {
      const res = await UserRegistrationService.getCitiesByState(sid);
      const list: SelectOption[] = (res?.data ?? []).map((x: any) => ({
        id: String(x.cityId ?? x.id ?? ""),
        name: String(x.cityName ?? x.name ?? ""),
      }));

      const cleaned = list.filter((x) => x.id && x.name);
      const hasOther = cleaned.some(
        (x) => String(x.id).toLowerCase() === OTHER_CITY_ID.toLowerCase()
      );

      setCities(
        hasOther
          ? sortCitiesWithOtherLast(cleaned)
          : sortCitiesWithOtherLast([...cleaned, OTHER_CITY_OPTION])
      );
    } catch {
      // even if API fails show Other
      setCities([OTHER_CITY_OPTION]);
    } finally {
      setLoadingCity(false);
    }
  };

  const isOtherCity = useMemo(
    () => !!cityId && String(cityId).toLowerCase() === String(OTHER_CITY_ID).toLowerCase(),
    [cityId]
  );

  const mpinMatch = useMemo(
    () => form.password.length === 6 && form.password === form.confirmMpin,
    [form.password, form.confirmMpin]
  );
  const resendTimeLabel = useMemo(() => {
    const minutes = Math.floor(resendTimer / 60);
    const seconds = resendTimer % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, [resendTimer]);
  const mobileOtpValid = useMemo(() => hasOtpValue(mobileOtp), [mobileOtp]);
  const emailOtpValid = useMemo(() => hasOtpValue(emailOtp), [emailOtp]);
  const canVerifyOtp = useMemo(() => {
    if (otpDeliveryInfo.mobile && otpDeliveryInfo.email) {
      return mobileOtpValid || emailOtpValid;
    }

    if (otpDeliveryInfo.mobile) {
      return mobileOtpValid;
    }

    if (otpDeliveryInfo.email) {
      return emailOtpValid;
    }

    return false;
  }, [emailOtpValid, mobileOtpValid, otpDeliveryInfo]);
  const otpVerificationHint = useMemo(() => {
    if (otpDeliveryInfo.mobile && otpDeliveryInfo.email) {
      return "Use any one valid 4-digit OTP to activate your account.";
    }

    return "Use the 4-digit OTP sent to your registered contact.";
  }, [otpDeliveryInfo]);
  const mobileOtpLabel = useMemo(
    () => getMaskedOtpLabel("Mobile OTP", otpDeliveryInfo.maskedMobileNumber),
    [otpDeliveryInfo.maskedMobileNumber]
  );
  const emailOtpLabel = useMemo(
    () => getMaskedOtpLabel("Email OTP", otpDeliveryInfo.maskedEmail),
    [otpDeliveryInfo.maskedEmail]
  );
  const keyboardAwareBottomPadding = useMemo(
    () => Math.max(110, keyboardHeight + 140),
    [keyboardHeight]
  );

  useEffect(() => {
    if (resendTimer <= 0) return;

    const timer = setInterval(() => {
      setResendTimer((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  useEffect(() => {
    const showEvent = Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent = Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";

    const showSubscription = Keyboard.addListener(showEvent, (event) => {
      setKeyboardHeight(event.endCoordinates?.height ?? 0);
    });
    const hideSubscription = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  const isFormValid = useMemo(() => {
    const firstOk = !!form.firstName.trim();
    const lastOk = !!form.lastName.trim();
    const dobOk = !!form.dobDate && !dobError;
    const mobileOk = !!form.mobileNumber.trim() && !mobileError && !getMobileValidationError(form.mobileNumber);
    const emailOk = !!form.email.trim() && !emailError && !getEmailValidationError(form.email);
    const profOk = !!form.profession.trim();
    const genderOk = !!genderId;
    const maritalOk = !!maritalStatusId;
    const addrOk = !!form.addressLine1.trim();
    const stateOk = !!stateId;
    const cityOk = !!cityId;
    const otherCityOk = !isOtherCity || !!form.cityName.trim();
    const mpinOk = mpinMatch;
    const termsOk = termsAccepted;

    return (
      firstOk &&
      lastOk &&
      mobileOk &&
      dobOk &&
      emailOk &&
      profOk &&
      genderOk &&
      maritalOk &&
      addrOk &&
      stateOk &&
      cityOk &&
      otherCityOk &&
      mpinOk &&
      termsOk
    );
  }, [
    form.firstName,
    form.lastName,
    form.mobileNumber,
    mobileError,
    form.dobDate,
    dobError,
    form.email,
    emailError,
    form.profession,
    genderId,
    maritalStatusId,
    form.addressLine1,
    stateId,
    cityId,
    isOtherCity,
    form.cityName,
    mpinMatch,
    termsAccepted,
  ]);

  const handleRegister = async () => {
    closeAllDropdowns();
    const isDobValid = validateDobField();
    const isMobileValid = validateMobileField();
    const isEmailValid = validateEmailField();

    if (!isFormValid || !isDobValid || !isMobileValid || !isEmailValid) {
      Flash.error("Please fill required fields and accept terms.");
      focusFirstInvalidRegisterField();
      return;
    }

    const payload: UserRegistrationModel = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      mobileNumber: form.mobileNumber.trim(),
      dob: form.dobDate ? format(form.dobDate, "yyyy-MM-dd") : null,
      email: form.email.trim() || null,
      profession: form.profession.trim() || null,

      // ✅ SEND Gender VALUE (not enumeration id)
      gender: genderId || null,

      maritalStatus: maritalStatusId || null,
      addressLine1: form.addressLine1.trim() || null,
      addressLine2: form.addressLine2.trim() ? form.addressLine2.trim() : null,
      stateId: stateId || null,
      cityId: cityId || null,

      // ✅ if not other => null
      cityName: isOtherCity ? (form.cityName.trim() || null) : null,

      password: form.password,
      familyDetail: form.familyDetail.trim() ? form.familyDetail.trim() : null,
    };

    setLoading(true);
    try {

      const res = await UserRegistrationService.register(payload);

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "Registration failed.");
        return;
      }

      if (!res?.token) {
        Flash.error("Verification session could not be created. Please try again.");
        return;
      }
      
      const otpInfo = normalizeOtpChannelInfo(res?.data.otpData);

      if (!hasAnyVerificationChannel(otpInfo)) {
        Flash.error("No verification channel is enabled for this account.");
        return;
      }

      setRegistrationPayload(payload);
      setRegistrationOtpToken(res.token);
      setOtpDeliveryInfo(otpInfo);
      setMobileOtp("");
      setEmailOtp("");
      setOtpErrorMessage("");
      setResendTimer(RESEND_OTP_SECONDS);
      setShowOtpModal(true);
      // Flash.success("Success!", "OTP sent successfully");
    } catch (e: any) {
      Flash.error(e?.message ?? "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {

    if (!registrationPayload) {
      Flash.error("Registration details missing. Please submit the form again.");
      return;
    }

    if (!registrationOtpToken) {
      return showOtpError("Verification session expired. Please submit the form again.");
    }

    setLoading(true);
    try {

      const res = await UserRegistrationService.resendOtp(registrationOtpToken);
      const otpInfo = normalizeOtpChannelInfo(res?.data);

      if (res?.status !== 200) {
        Flash.error(res?.message ?? "OTP resend failed.");
        return;
      }

      if (!res?.token) {
        Flash.error("Verification session could not be refreshed. Please submit again.");
        return;
      }

      if (!hasAnyVerificationChannel(otpInfo)) {
        Flash.error("No verification channel is enabled for this account.");
        return;
      }

      setRegistrationOtpToken(res.token);
      setOtpDeliveryInfo(otpInfo);
      setMobileOtp("");
      setEmailOtp("");
      setOtpErrorMessage("");
      setResendTimer(RESEND_OTP_SECONDS);
      Flash.success("Success!", "OTP sent successfully");
    } catch (e: any) {
      Flash.error(e?.message ?? "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {

    if (!registrationOtpToken) {
      return showOtpError("Verification session expired. Please submit the form again.");
    }

    const payload: RegistrationOtpModel = {};

    if (otpDeliveryInfo.mobile && mobileOtpValid) {
      payload.mobileOtp = mobileOtp.trim();
    }

    if (otpDeliveryInfo.email && emailOtpValid) {
      payload.emailOtp = emailOtp.trim();
    }

    if (!payload.mobileOtp && !payload.emailOtp) {
      if (otpDeliveryInfo.mobile && otpDeliveryInfo.email) {
        return showOtpError("Enter at least one valid OTP to continue.");
      }

      if (otpDeliveryInfo.mobile) {
        return showOtpError("Enter the mobile OTP.");
      }

      return showOtpError("Enter the email OTP.");
    }

    setLoading(true);
    setOtpErrorMessage("");
    try {
      const res = await UserRegistrationService.verifyOtp(payload, registrationOtpToken);

      if (res?.status !== 200 && res?.status != 401) {
        showOtpError(res?.message ?? "OTP verification failed.");
        return;
      }

      resetRegistrationOtpState();
      await new Promise((resolve) => setTimeout(resolve, 180));

      Flash.success(
        "Hare Krishna!",
        "Thank you for registering. Your approval is pending. You will be notified once approved."
      );

      // 👉 ensure no session saved
      await logout({ deleteDevice: false });

      // 👉 redirect to login
      navigation.navigate("Login");
    } catch (e: any) {
      showOtpError(e?.message ?? "OTP verification error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DivineBackground overlayColor="#FFF8E7" overlayOpacity={0.88}>
      <Header title="Register" showBack showProfile={false} />

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={Platform.OS === "ios" ? 18 : 24}
      >
        <ScrollView
          contentContainerStyle={[styles.scroll, { paddingBottom: keyboardAwareBottomPadding }]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={styles.card}>
            <Text style={styles.h2}>Create Account</Text>
            <Text style={styles.sub}>Join the spiritual community</Text>

            <Input
              label="First Name *"
              placeholder="First Name"
              value={form.firstName}
              onChangeText={(t) => setField("firstName", t)}
              onFocus={closeAllDropdowns}
            />

            <Input
              label="Last Name *"
              placeholder="Last Name"
              value={form.lastName}
              onChangeText={(t) => setField("lastName", t)}
              onFocus={closeAllDropdowns}
            />

            <View onLayout={setFieldLayout("mobileNumber")}>
              <Input
                ref={setInputRef("mobileNumber")}
                label="Mobile Number *"
                placeholder="10 digit mobile"
                keyboardType="number-pad"
                maxLength={10}
                value={form.mobileNumber}
                onChangeText={syncMobileField}
                onFocus={closeAllDropdowns}
                onBlur={validateMobileField}
              />
              {mobileError ? <Text style={styles.errorText}>{mobileError}</Text> : null}
            </View>

            <View onLayout={setFieldLayout("dob")}>
              <Input
                ref={setInputRef("dob")}
                label="Date of Birth *"
                placeholder={DOB_PLACEHOLDER}
                keyboardType="number-pad"
                maxLength={10}
                value={form.dob}
                onChangeText={syncDobField}
                onFocus={closeAllDropdowns}
                onBlur={validateDobField}
              />
              {dobError ? <Text style={styles.errorText}>{dobError}</Text> : null}
            </View>

            <View onLayout={setFieldLayout("email")}>
              <Input
                ref={setInputRef("email")}
                label="Email *"
                placeholder="your@email.com"
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                value={form.email}
                onChangeText={syncEmailField}
                onFocus={closeAllDropdowns}
                onBlur={validateEmailField}
              />
              {emailError ? <Text style={styles.errorText}>{emailError}</Text> : null}
            </View>

            <Input
              label="Profession *"
              placeholder="e.g. Engineer, Doctor, Business"
              value={form.profession}
              onChangeText={(t) => setField("profession", t)}
              onFocus={closeAllDropdowns}
            />

            {/* ✅ Gender (STACK) */}
            <SearchSelect
              label="Gender"
              required
              placeholder={loadingGender ? "Loading..." : "Select Gender"}
              valueLabel={genderLabel}
              valueId={genderId || null}
              options={genders}
              disabled={loadingGender || showOtpModal}
              isOpen={openGender}
              onToggle={() => {
                if (showOtpModal) return;
                Keyboard.dismiss();
                setOpenGender((p) => !p);
                setOpenMarital(false);
                setOpenState(false);
                setOpenCity(false);
              }}
              onSelect={(opt) => {
                closeAllDropdowns();
                setGenderLabel(opt.name);
                setGenderValue(opt.id); // ✅ send value
              }}
              searchPlaceholder="Search Gender..."
            />

            {/* ✅ Marital (STACK) */}
            <SearchSelect
              label="Marital Status"
              required
              placeholder={loadingMarital ? "Loading..." : "Select Status"}
              valueLabel={maritalLabel}
              valueId={maritalStatusId}
              options={maritals}
              disabled={loadingMarital || showOtpModal}
              isOpen={openMarital}
              onToggle={() => {
                if (showOtpModal) return;
                Keyboard.dismiss();
                setOpenMarital((p) => !p);
                setOpenGender(false);
                setOpenState(false);
                setOpenCity(false);
              }}
              onSelect={(opt) => {
                closeAllDropdowns();
                setMaritalStatusId(opt.id);
                setMaritalLabel(opt.name);
              }}
              searchPlaceholder="Search Status..."
            />

            {/* Address section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Address Details</Text>

              <Input
                label="Address Line 1 *"
                placeholder="House No, Building, Street"
                value={form.addressLine1}
                onChangeText={(t) => setField("addressLine1", t)}
                onFocus={closeAllDropdowns}
              />

              <Input
                label="Address Line 2"
                placeholder="Area, Landmark (Optional)"
                value={form.addressLine2}
                onChangeText={(t) => setField("addressLine2", t)}
                onFocus={closeAllDropdowns}
              />

              <SearchSelect
                label="State"
                required
                placeholder={loadingState ? "Loading..." : "Select State"}
                valueLabel={stateName}
                valueId={stateId}
                options={states}
                disabled={loadingState || showOtpModal}
                isOpen={openState}
                onToggle={() => {
                  if (showOtpModal) return;
                  Keyboard.dismiss();
                  setOpenState((p) => !p);
                  setOpenCity(false);
                  setOpenGender(false);
                  setOpenMarital(false);
                }}
                dropdownDirection="up"
                onSelect={async (opt) => {
                  closeAllDropdowns();

                  setStateId(opt.id);
                  setStateName(opt.name);

                  // reset city
                  setCityId(null);
                  setCityLabel("");
                  setCities([]);
                  setField("cityName", ""); // ✅ clear always on state change

                  await loadCitiesByState(opt.id);
                }}
                searchPlaceholder="Search State..."
              />

              <SearchSelect
                label="City"
                required
                placeholder={
                  !stateId ? "Select State First" : loadingCity ? "Loading..." : "Select City"
                }
                valueLabel={cityLabel}
                valueId={cityId}
                options={cities}
                alwaysVisibleOptionIds={[OTHER_CITY_ID]}
                disabled={!stateId || loadingCity || showOtpModal}
                isOpen={openCity}
                onToggle={() => {
                  if (showOtpModal) return;
                  if (!stateId || loadingCity) return;
                  Keyboard.dismiss();
                  setOpenCity((p) => !p);
                  setOpenState(false);
                  setOpenGender(false);
                  setOpenMarital(false);
                }}
                dropdownDirection="up"
                onSelect={(opt) => {
                  closeAllDropdowns();
                  setCityId(opt.id);
                  setCityLabel(opt.name);

                  const isOther = String(opt.id).toLowerCase() === OTHER_CITY_ID.toLowerCase();
                  if (!isOther) {
                    setField("cityName", ""); // ✅ clear if not Other
                  }
                }}
                searchPlaceholder="Search City..."
              />

              {/* ✅ Show only when City = Other */}
              {isOtherCity ? (
                <Input
                  label="City Name *"
                  placeholder="Enter your city name"
                  value={form.cityName}
                  onChangeText={(t) => setField("cityName", t)}
                  onFocus={closeAllDropdowns}
                />
              ) : null}

              {/* ✅ Family Details multiline looks correct */}
              <Input
                label="Family Details (Optional)"
                placeholder="Brief details about family members"
                value={form.familyDetail}
                onChangeText={(t) => setField("familyDetail", t)}
                onFocus={closeAllDropdowns}
                multiline
                numberOfLines={4}
                style={{ minHeight: 90, textAlignVertical: "top", paddingVertical: 10 }}
              />
            </View>

            {/* MPIN */}
            <View style={styles.row2}>
              <View style={[styles.mpinWrap, { flex: 1 }]}>
                <Input
                  label="Set MPIN *"
                  placeholder="6 digits"
                  keyboardType="number-pad"
                  secureTextEntry={!showPassword}
                  maxLength={6}
                  value={form.password}
                  onChangeText={(t) => setField("password", t.replace(/\D/g, ""))}
                  onFocus={closeAllDropdowns}
                  style={{ paddingRight: 40 }}
                />

                <Pressable
                  style={styles.eyeIcon}
                  onPress={() => setShowPassword(!showPassword)}
                >
                  <Ionicons
                    name={showPassword ? "eye-off-outline" : "eye-outline"}
                    size={22}
                    color="#6B7280"
                  />
                </Pressable>
              </View>

              <View style={[styles.mpinWrap, { flex: 1 }]}>
                <Input
                  label="Confirm MPIN *"
                  placeholder="6 digits"
                  keyboardType="number-pad"
                  secureTextEntry={!showConfirmPassword}
                  maxLength={6}
                  value={form.confirmMpin}
                  onChangeText={(t) => setField("confirmMpin", t.replace(/\D/g, ""))}
                  onFocus={closeAllDropdowns}
                  style={{ paddingRight: 40 }}
                />

                <Pressable
                  style={styles.eyeIcon}
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <Ionicons
                    name={showConfirmPassword ? "eye-off-outline" : "eye-outline"}
                    size={22}
                    color="#6B7280"
                  />
                </Pressable>
              </View>
            </View>

            {!mpinMatch && (form.password.length > 0 || form.confirmMpin.length > 0) ? (
              <Text style={styles.errorText}>MPIN and Confirm MPIN must match (6 digits).</Text>
            ) : null}

            {/* Terms */}
            <View style={styles.termsRow}>
              <Pressable
                onPress={() => setTermsAccepted((p) => !p)}
                style={[styles.checkbox, termsAccepted ? styles.checkboxOn : null]}
              >
                {termsAccepted ? <Text style={styles.checkMark}>✓</Text> : null}
              </Pressable>

              <Text style={styles.termsText}>
                I accept the{" "}
                <Text style={styles.termsLink} onPress={() => setShowTerms(true)}>
                  Terms and Conditions
                </Text>{" "}
                and{" "}
                <Text
                  style={styles.termsLink}
                  onPress={() => Linking.openURL("https://gaurpremaashray.co.in/privacy-policy.html")}
                >
                  Privacy Policy
                </Text>
                , and agree to share my sadhana data with my spiritual guides.
              </Text>
            </View>

            <Button
              title={loading ? "Processing..." : "Next"}
              onPress={handleRegister}
              disabled={loading || !isFormValid}
              style={{ marginTop: 10, opacity: loading || !isFormValid ? 0.6 : 1 }}
            />
          </View>
        </ScrollView>

        {/* ✅ OTP Modal */}
        <Modal
          visible={showOtpModal}
          transparent
          animationType="fade"
          onRequestClose={() => { }}
          presentationStyle="overFullScreen"
          statusBarTranslucent
        >
          <View style={styles.otpOverlay}>
            <BlurBackdrop />
            <ToastHost />

            <View style={styles.otpContent}>
              <View style={styles.otpCard}>
                <View style={styles.otpIconWrap}>
                  <Text style={{ fontSize: 22 }}>📱</Text>
                </View>

                <Text style={styles.otpTitle}>Verify Your Account</Text>
                <Text style={styles.otpSub}>Enter the 4-digit OTP to verify your account.</Text>
                <Text style={styles.otpHint}>{otpVerificationHint}</Text>

                {otpErrorMessage ? (
                  <Text style={styles.otpErrorText}>{otpErrorMessage}</Text>
                ) : null}

                <View style={styles.otpInputWrap}>
                  {otpDeliveryInfo.mobile ? (
                    <Input
                      label={mobileOtpLabel}
                      placeholder={OTP_PLACEHOLDER}
                      keyboardType="number-pad"
                      maxLength={OTP_MAX_LENGTH}
                      value={mobileOtp}
                      onChangeText={(t) => {
                        setMobileOtp(t.replace(/\D/g, ""));
                        if (otpErrorMessage) {
                          setOtpErrorMessage("");
                        }
                      }}
                      textAlign="center"
                      style={styles.otpInputFull}
                    />
                  ) : null}

                  {otpDeliveryInfo.email ? (
                    <Input
                      label={emailOtpLabel}
                      placeholder={OTP_PLACEHOLDER}
                      keyboardType="number-pad"
                      maxLength={OTP_MAX_LENGTH}
                      value={emailOtp}
                      onChangeText={(t) => {
                        setEmailOtp(t.replace(/\D/g, ""));
                        if (otpErrorMessage) {
                          setOtpErrorMessage("");
                        }
                      }}
                      textAlign="center"
                      style={styles.otpInputFull}
                    />
                  ) : null}
                </View>

                {resendTimer > 0 ? (
                  <Text style={styles.otpTimerText}>Resend OTP in {resendTimeLabel}</Text>
                ) : (
                  <Pressable
                    onPress={handleResendOtp}
                    disabled={loading}
                    style={{ marginTop: 2 }}
                  >
                    <Text style={[styles.otpResendText, loading ? { opacity: 0.6 } : null]}>
                      {loading ? "Sending..." : "Resend OTP"}
                    </Text>
                  </Pressable>
                )}

                <View style={styles.otpButtonWrap}>
                  <Button
                    title={loading ? "Verifying..." : "Verify & Login"}
                    onPress={handleVerifyOtp}
                    disabled={loading || !canVerifyOtp}
                    style={[
                      styles.otpButtonFull,
                      { opacity: loading || !canVerifyOtp ? 0.6 : 1 },
                    ]}
                  />
                </View>

                <Pressable
                  onPress={handleBackToLogin}
                  style={{ marginTop: 12 }}
                >
                  <Text style={styles.backText}>Back to login</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>

        {/* ✅ Terms Modal */}
        <Modal
          visible={showTerms}
          transparent
          animationType="fade"
          onRequestClose={() => setShowTerms(false)}
        >
          <Pressable style={styles.modalBackdrop} onPress={() => setShowTerms(false)} />
          <ToastHost />
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Terms & Conditions</Text>
              <Pressable onPress={() => setShowTerms(false)} hitSlop={10}>
                <Text style={styles.modalClose}>×</Text>
              </Pressable>
            </View>

            <ScrollView style={styles.modalBody} showsVerticalScrollIndicator={false}>
              <Text style={styles.tcText}>
                <Text style={styles.tcStrong}>1. Spiritual Purpose: </Text>
                This application is designed solely for tracking and improving your personal sadhana
                in Krishna Consciousness.
              </Text>

              <Text style={styles.tcText}>
                <Text style={styles.tcStrong}>2. Data Privacy: </Text>
                We collect your name, mobile number, email, date of birth, gender, address, and
                sadhana/health records to operate the app. All personal data is stored in encrypted
                form. Your sadhana reports may be visible to your selected Guru/Temple authorities
                for guidance. We do not sell your data or share it with advertisers. For full
                details, please read our{" "}
                <Text
                  style={styles.termsLink}
                  onPress={() => Linking.openURL("https://gaurpremaashray.co.in/privacy-policy.html")}
                >
                  Privacy Policy
                </Text>
                .
              </Text>

              <Text style={styles.tcText}>
                <Text style={styles.tcStrong}>3. Data Deletion: </Text>
                You may delete your account and all associated data at any time from the Edit
                Profile screen. Data deletion requests can also be sent to
                info@gaurpremaashray.co.in.
              </Text>

              <Text style={styles.tcText}>
                <Text style={styles.tcStrong}>4. Accuracy: </Text>
                Users are expected to enter truthful data for chanting, reading, and other services.
              </Text>

              <Text style={styles.tcText}>
                <Text style={styles.tcStrong}>5. Conduct: </Text>
                Misuse or disrespectful behavior may result in account suspension.
              </Text>

              <Text style={styles.tcText}>
                <Text style={styles.tcStrong}>6. Communication: </Text>
                We may send reminders/quotes via notifications or SMS to assist your practice.
              </Text>

              <Text style={styles.tcSmall}>Last updated: March 17, 2026</Text>
            </ScrollView>

            <View style={styles.modalFooter}>
              <Button
                title="Accept & Close"
                onPress={() => {
                  setTermsAccepted(true);
                  setShowTerms(false);
                }}
              />
            </View>
          </View>
        </Modal>
      </KeyboardAvoidingView>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  scroll: { flexGrow: 1, padding: 16, paddingBottom: 110, alignItems: "center" },

  card: {
    width: "100%",
    maxWidth: 430,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderRadius: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.20)",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 10 },
    elevation: 6,
    gap: 12,
  },

  h2: { fontSize: 20, fontWeight: "900", color: "#4A2311", textAlign: "center" },
  sub: {
    marginTop: -6,
    fontSize: 12.5,
    fontWeight: "700",
    color: "rgba(74,35,17,0.55)",
    textAlign: "center",
  },

  row2: { flexDirection: "row", gap: 12 },

  section: {
    gap: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "rgba(251,146,60,0.18)",
  },
  sectionTitle: { fontSize: 13, fontWeight: "900", color: "rgba(234,88,12,0.90)" },

  errorText: { fontSize: 12, fontWeight: "800", color: "rgba(239,68,68,0.95)" },

  // Terms
  termsRow: { flexDirection: "row", gap: 10, alignItems: "flex-start", marginTop: 2 },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 5,
    borderWidth: 1.5,
    borderColor: "rgba(251,146,60,0.55)",
    backgroundColor: "rgba(255,255,255,0.9)",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 2,
  },
  checkboxOn: {
    backgroundColor: "rgba(249,115,22,0.95)",
    borderColor: "rgba(249,115,22,0.95)",
  },
  checkMark: { color: "#fff", fontWeight: "900", fontSize: 12 },
  termsText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 18,
    color: "rgba(55,65,81,0.95)",
    fontWeight: "600",
  },
  termsLink: { color: "rgba(234,88,12,0.95)", fontWeight: "900" },

  // Date field (DOB)
  dateField: {
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.28)",
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dateText: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.92)" },
  dateIcon: { fontSize: 16 },

  // Dropdown
  ddWrap: { gap: 6, position: "relative", zIndex: 1 },
  ddWrapOpen: { zIndex: 2000 },

  ddLabel: { fontSize: 12, fontWeight: "900", color: "#4A2311" },
  ddReq: { color: "rgba(239,68,68,0.95)" },

  ddField: {
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.28)",
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ddFieldOpen: { borderColor: "rgba(249,115,22,0.65)" },
  ddFieldDisabled: { opacity: 0.55 },

  ddValue: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.92)" },
  ddPlaceholder: { color: "rgba(107,114,128,0.85)" },
  ddChevron: { fontSize: 16, fontWeight: "900", color: "rgba(234,88,12,0.95)" },

  ddFloatCardDown: { position: "absolute", left: 0, right: 0, top: 74 },
  ddFloatCardUp: { position: "absolute", left: 0, right: 0, bottom: 74 },
  ddListCard: {
    backgroundColor: "#fff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.20)",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
  },
  ddSearchWrap: { padding: 10, borderBottomWidth: 1, borderBottomColor: "rgba(251,146,60,0.10)" },
  ddSearchInput: { marginTop: -6 },
  ddScroll: { maxHeight: 240 },

  ddItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(251,146,60,0.10)",
  },
  ddItemSelected: { backgroundColor: "rgba(255,237,213,0.65)" },

  ddRadioRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  ddRadio: {
    width: 14,
    height: 14,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: "rgba(234,88,12,0.55)",
    backgroundColor: "transparent",
  },
  ddRadioOn: { backgroundColor: "rgba(234,88,12,0.95)", borderColor: "rgba(234,88,12,0.95)" },

  ddItemText: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.92)" },
  ddItemTextSelected: { color: "rgba(124,45,18,0.95)", fontWeight: "900" },

  ddEmpty: { padding: 12, fontSize: 12, fontWeight: "800", color: "rgba(107,114,128,0.85)" },

  // OTP
  otpOverlay: { flex: 1 },
  otpContent: {
    flex: 1,
    justifyContent: "flex-start",
    paddingTop: 140,
    paddingHorizontal: 16,
  },
  otpBackdropFallback: { ...StyleSheet.absoluteFillObject, backgroundColor: "rgba(0,0,0,0.35)" },
  otpCard: {
    alignSelf: "center",
    width: "100%",
    maxWidth: 360,
    backgroundColor: "rgba(255,255,255,0.94)",
    borderRadius: 18,
    padding: 18,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.20)",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 10 },
    elevation: 8,
    alignItems: "center",
    gap: 10,
  },
  otpIconWrap: {
    width: 60,
    height: 60,
    borderRadius: 999,
    backgroundColor: "rgba(34,197,94,0.15)",
    alignItems: "center",
    justifyContent: "center",
  },
  otpTitle: { fontSize: 18, fontWeight: "900", color: "#4A2311" },
  otpSub: {
    fontSize: 13.5,
    fontWeight: "700",
    color: "rgba(74,35,17,0.60)",
    textAlign: "center",
    marginBottom: 2,
  },
  otpHint: {
    fontSize: 12,
    fontWeight: "700",
    color: "rgba(74,35,17,0.50)",
    textAlign: "center",
    marginBottom: 10,
  },
  otpErrorText: {
    width: "100%",
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(239,68,68,0.95)",
    textAlign: "center",
    marginBottom: 2,
  },
  otpInputWrap: { width: "100%", gap: 10 },
  otpButtonWrap: { width: "100%", marginTop: 10 },
  otpInputFull: {
    width: "100%",
    fontSize: 18,
    fontWeight: "700",
    letterSpacing: 6,
    textAlign: "center",
  },
  otpButtonFull: { width: "100%" },
  otpTimerText: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(74,35,17,0.55)",
    textAlign: "center",
  },
  otpResendText: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: "800",
    color: "rgba(234,88,12,0.95)",
    textAlign: "center",
  },
  backText: { fontSize: 12, fontWeight: "800", color: "rgba(107,114,128,0.9)" },

  // Modal common
  modalBackdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: "rgba(0,0,0,0.50)" },

  // iOS Date
  iosDateCard: {
    position: "absolute",
    left: 16,
    right: 16,
    bottom: 16,
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 14,
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.25)",
  },
  iosDateTitle: { fontSize: 14, fontWeight: "900", color: "#4A2311", marginBottom: 10 },

  mpinWrap: {
    position: "relative",
  },

  eyeIcon: {
    position: "absolute",
    right: 12,
    bottom: 12,
  },

  // Terms modal
  modalCard: {
    position: "absolute",
    left: 16,
    right: 16,
    top: 90,
    bottom: 90,
    backgroundColor: "#fff",
    borderRadius: 16,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(251,146,60,0.25)",
    shadowColor: "#000",
    shadowOpacity: 0.20,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 12 },
    elevation: 10,
  },
  modalHeader: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: "rgba(255,237,213,0.65)",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(251,146,60,0.18)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  modalTitle: { fontSize: 16, fontWeight: "900", color: "#4A2311" },
  modalClose: { fontSize: 26, fontWeight: "900", color: "rgba(239,68,68,0.95)", marginTop: -4 },
  modalBody: { padding: 14 },
  tcText: { fontSize: 12.5, lineHeight: 18, color: "rgba(55,65,81,0.95)", fontWeight: "600", marginBottom: 10 },
  tcStrong: { fontWeight: "900", color: "rgba(17,24,39,0.95)" },
  tcSmall: { marginTop: 10, fontSize: 11, fontWeight: "700", color: "rgba(107,114,128,0.85)", fontStyle: "italic" },
  modalFooter: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(251,146,60,0.18)",
    backgroundColor: "rgba(249,250,251,0.95)",
  },
});



